/**
 * Which of our shops a Google Shopping seller is, if any.
 *
 * Google's `source` field is whatever the merchant registered, and it is not
 * consistent even within one response: the first live call for "hajmola"
 * returned BOTH `amazon.in` and `Amazon.in` as separate sellers. Matching on
 * the raw string would have shown two Amazons with different prices and no way
 * to tell which button either belonged to.
 *
 * Deliberately a small allow-list rather than fuzzy matching. A wrong match
 * puts one shop's price under another shop's logo, which is worse than no price
 * at all — the number is only useful because it is attributable.
 */
/** Everything that has been seen, or is likely, for each shop. */
const ALIASES = {
    blinkit: ["blinkit", "blinkit.com", "grofers"],
    zepto: ["zepto", "zeptonow", "zeptonow.com", "zepto.com"],
    // Swiggy sells food and groceries under two names; both land on Instamart's
    // search, which is the only Swiggy address this app links to.
    instamart: ["instamart", "swiggy instamart", "swiggy", "swiggy.com"],
    // Google names this one "bigbasket.com" and the app calls itself "bigbasket".
    bigbasket: ["bigbasket", "bigbasket.com", "big basket", "bb daily", "bbnow"],
    // Seen in the wild as "JioMart Grocery" — the qualifier is the department,
    // not a different shop.
    jiomart: ["jiomart", "jiomart grocery", "jiomart.com", "reliance jiomart"],
    // "DMart Ready" is the delivery arm and the name the listings carry.
    dmart: ["dmart", "dmart ready", "dmart.in", "d mart", "avenue supermarts"],
    flipkart: ["flipkart", "flipkart.com", "flipkart minutes"],
    amazon: ["amazon", "amazon.in", "amazon india", "www.amazon.in"],
};
/** Reversed once, at module load, so a lookup is not a scan per shop. */
const BY_ALIAS = new Map(Object.entries(ALIASES).flatMap(([id, names]) => names.map((n) => [n, id])));
/**
 * Normalises a seller name to its shop, or null for the many we do not link.
 *
 * PharmEasy, bigbasket and StarQuik are real sellers with real prices and no
 * button here, and null is the honest answer for them — the alternative is
 * inventing a destination for a price.
 */
export function storeOf(source) {
    const key = source
        .toLowerCase()
        .replace(/^www\./, "")
        .replace(/\s+/g, " ")
        .trim();
    return BY_ALIAS.get(key) ?? null;
}
//# sourceMappingURL=sellers.js.map